import React, { RefObject, useRef, useState } from "react";

import { FormBox, FormField, FormLabel } from "@impero-com/ui";
import { InputNumber, InputNumberProps } from "antd";
import { cx } from "class-variance-authority";

import { ERRORED_CLASSNAME } from "../../../utils/form/erroredElements";
import { t } from "../../../utils/i18n";
import { mergeRefs } from "../../../utils/mergeRefs";
import { parseNumberOrNull } from "../../../utils/numeric";

import * as theme from "./theme.css";

export interface NumberInputBaseProps {
    className?: string;
    label?: React.ReactNode;
    disabled?: boolean;
    // The text rendered when the value is null
    placeholder?: string;
    // normal (default): A regular input, with borders, gray-background when disabled
    // borderless: An input without borders, unless focused
    styleVariant?: "normal" | "borderless";
    // The value granularity
    step?: number;
    // Gives focus to this component when mounted
    autoFocus?: boolean;
    addonBefore?: React.ReactNode;
    addonAfter?: React.ReactNode;
    precision?: number;
}

export interface NumberInputProps extends NumberInputBaseProps {
    value?: number | null;
    onChange?: (value: number | null) => void;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    fieldRef?: React.RefCallback<HTMLElement>;
    min?: number;
    max?: number;
    required?: boolean;
    error?: string;
}

/**
 * Basic form component based on ant design's InputNumber
 *
 * This component leverages the following constraints from the validator:
 *  - `min(n)` is mapped to `min={n}`
 *  - `max(n)` is mapped to `max={n}`
 *  - `required` is mapped to `required`
 */
export function NumberInput({
    className,
    label,
    placeholder,
    step,
    addonAfter,
    addonBefore,
    precision,
    value,
    error,
    onChange,
    fieldRef,
    onBlur,
    min,
    max,
    required,
    disabled = false,
    styleVariant = "normal",
    autoFocus = false,
}: NumberInputProps) {
    const [editing, setEditing] = useState(false);
    const inputRef: RefObject<HTMLInputElement | null> = useRef(null);

    const displayBorder =
        styleVariant !== "borderless" || editing || error !== undefined;

    const inputClasses = cx(className, theme.numberInput, {
        [theme.borderless]: !displayBorder,
    });

    const handleBlur: InputNumberProps["onBlur"] = (event) => {
        setEditing(false);
        // When the value is "invalid" (like not in range),
        // antd tries to be clever and don't fire onChange events until the next blur.
        // The issue is this late onChange call runs after this one,
        // so let's wait for the next frame before dispatching.
        //
        // I hate JS, I hate JS frameworks, I hate cleverness.
        // -- GO.
        onBlur?.(event);
        setTimeout(() => onChange?.(parseNumberOrNull(event.target.value)));
    };

    return (
        <FormBox>
            <FormField
                error={error}
                lite={label === undefined}
                className={cx(error && ERRORED_CLASSNAME)}
            >
                {label && (
                    <FormLabel
                        disabled={disabled}
                        showPencil={styleVariant === "borderless"}
                        onClick={() => inputRef.current?.focus()}
                    >
                        {label}
                    </FormLabel>
                )}
                <InputNumber
                    ref={mergeRefs(inputRef, fieldRef)}
                    value={value}
                    disabled={disabled}
                    autoFocus={autoFocus}
                    className={inputClasses}
                    placeholder={
                        placeholder ??
                        (!displayBorder ? t("Form_No_Field_Set") : undefined)
                    }
                    bordered={displayBorder}
                    controls={displayBorder}
                    onChange={onChange}
                    onFocus={() => setEditing(true)}
                    onBlur={handleBlur}
                    onPressEnter={() => inputRef.current?.blur()}
                    status={error !== undefined ? "error" : ""}
                    step={step}
                    min={min}
                    max={max}
                    required={required}
                    precision={precision}
                    addonAfter={addonAfter}
                    addonBefore={addonBefore}
                />
            </FormField>
        </FormBox>
    );
}
